import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Paper,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';

const ValuationMomentumMatrix = ({ sectors, momentum }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  if (!sectors || sectors.length === 0) {
    return null;
  }

  // Helper function to get sector name based on current language
  const getSectorName = (sector) => {
    if (currentLang === 'vi' && sector.sector_name_vi) {
      return sector.sector_name_vi;
    }
    return sector.sector_name_en || sector.sector_name || sector.valuation_sector;
  };

  // Combine sector data with momentum
  const momentumMap = new Map();
  
  if (momentum) {
    [...(momentum.rising_sectors || []), ...(momentum.falling_sectors || [])].forEach(m => {
      momentumMap.set(m.sector_name, m.score_change || 0);
    });
  }

  // Categorize sectors into 4 quadrants
  const avgValuation = sectors.reduce((sum, s) => sum + parseFloat(s.val_score || 50), 0) / sectors.length;
  
  const quadrants = {
    valueMomentum: [], // High valuation + Rising momentum
    growthMomentum: [], // Low valuation + Rising momentum (Value plays)
    valueStable: [], // High valuation + Falling momentum
    avoid: [], // Low valuation + Falling momentum
  };

  sectors.forEach(sector => {
    const valScore = parseFloat(sector.val_score || 50);
    const scoreChange = momentumMap.get(sector.sector_name) || 0;
    const isHighVal = valScore >= avgValuation;
    const isRising = scoreChange > 0;

    const item = {
      name: sector.sector_name,
      displayName: getSectorName(sector),
      valScore,
      scoreChange,
      finalScore: parseFloat(sector.final_score || 0),
      recommendation: sector.recommendation,
    };

    if (isHighVal && isRising) {
      quadrants.valueMomentum.push(item);
    } else if (!isHighVal && isRising) {
      quadrants.growthMomentum.push(item);
    } else if (isHighVal && !isRising) {
      quadrants.valueStable.push(item);
    } else {
      quadrants.avoid.push(item);
    }
  });

  // Sort each quadrant by final score
  Object.keys(quadrants).forEach(key => {
    quadrants[key].sort((a, b) => b.finalScore - a.finalScore);
  });

  const QuadrantCard = ({ title, items, color, icon, description }) => (
    <Box sx={{ flex: 1 }}>
      <Paper
        sx={{
          p: 2,
          height: '100%',
          background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
          border: `2px solid ${color}`,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Box sx={{ ml: 1, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Box>
          <Chip label={items.length} sx={{ bgcolor: color, color: 'white', fontWeight: 700 }} />
        </Box>

        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('dashboard.valuationMomentum.noSectors')}
            </Typography>
          ) : (
            items.slice(0, 5).map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  borderBottom: idx < items.length - 1 ? '1px solid #e0e0e0' : 'none',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.displayName || item.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('dashboard.valuationMomentum.val')}: {item.valScore.toFixed(1)} | {t('dashboard.valuationMomentum.delta')}: {item.scoreChange > 0 ? '+' : ''}{item.scoreChange.toFixed(1)}
                  </Typography>
                </Box>
                <Chip
                  label={item.recommendation || 'N/A'}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                    bgcolor: item.recommendation === 'Strong Buy' ? '#10b981' :
                             item.recommendation === 'Buy' ? '#3b82f6' :
                             item.recommendation === 'Hold' ? '#f59e0b' :
                             item.recommendation === 'Underweight' ? '#f97316' :
                             item.recommendation === 'Avoid' ? '#ef4444' :
                             item.recommendation === 'Sell' ? '#dc2626' : '#6b7280',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Box>
            ))
          )}
        </Box>
      </Paper>
    </Box>
  );

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <ShowChartIcon sx={{ fontSize: 32, mr: 1, color: '#667eea' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('dashboard.valuationMomentum.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.valuationMomentum.subtitle')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <QuadrantCard
              title={t('dashboard.valuationMomentum.qualityGrowth')}
              items={quadrants.valueMomentum}
              color="#11998e"
              icon={<TrendingUpIcon sx={{ color: '#11998e' }} />}
              description={t('dashboard.valuationMomentum.qualityGrowthDesc')}
            />
            <QuadrantCard
              title={t('dashboard.valuationMomentum.valuePlays')}
              items={quadrants.growthMomentum}
              color="#667eea"
              icon={<TrendingUpIcon sx={{ color: '#667eea' }} />}
              description={t('dashboard.valuationMomentum.valuePlaysDesc')}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <QuadrantCard
              title={t('dashboard.valuationMomentum.stableValue')}
              items={quadrants.valueStable}
              color="#ffa726"
              icon={<TrendingDownIcon sx={{ color: '#ffa726' }} />}
              description={t('dashboard.valuationMomentum.stableValueDesc')}
            />
            <QuadrantCard
              title={t('dashboard.valuationMomentum.cautionZone')}
              items={quadrants.avoid}
              color="#ff6b6b"
              icon={<TrendingDownIcon sx={{ color: '#ff6b6b' }} />}
              description={t('dashboard.valuationMomentum.cautionZoneDesc')}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ValuationMomentumMatrix;

